import CRMStatusChip from '@components/Common/CRMStatusChip';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import { EntityWorkspace, IdentityRail, SectionCard, WorkspaceField } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import RecordPickerDialog from '@components/Common/RecordPickerDialog';
import type { PickerRow } from '@components/Common/RecordPickerDialog';
import { applicantLabel } from '@components/Common/Utils/filterLinks';
import { approveCompanyRequest, assignUnownedCompanyJobs, companyApplicationCounts, companyById, companyJobsForAssignment, companyJobsCount, companyRequestById, countUnownedCompanyJobs, employersByCompany, getEmployerById, setEmployerJobsOnCompany, updateCompanyRequestStatus } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { REJECT_TITLE, isPendingRequest, rejectBody } from './decision';
import { formatDate, formatDateTime } from '@helpers/date';

/**
 * A company-join request: an employer asking to be attached to a company record.
 *
 * Reached only by deep link: nothing in the console navigates here, the link arrives by email.
 * That is how two things went unnoticed for a long time. It rendered none of the three documented
 * layouts, hand-rolling `.page-header` / `.page-title` inside a raw MUI Container; and its "Back to
 * Company Requests" button pointed at `/company-requests`, a path no route declares, so the one
 * navigation control on the page landed on Not Found.
 *
 * There is no list of company requests to go back to, so back goes to Companies, which is the
 * nearest real destination and is where the approved request ends up mattering.
 */
const CompanyRequests = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<any>(null);
  const [employerEmail, setEmployerEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [activityOpen, setActivityOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  /*
   * Who already holds this company, and whether their jobs should come across.
   *
   * A claim is very often a handover rather than a first registration: the person who registered
   * the company has left, or accounts are being consolidated. Approving without moving the jobs
   * leaves those listings owned by an account nobody uses, so the new employer cannot edit them
   * or see their applicants, and closing and re-posting would lose every application against
   * them. Offered here rather than as a separate screen because this is the moment the admin has
   * the context to decide.
   */
  const [existingHolders, setExistingHolders] = useState<any[]>([]);
  const [transferJobs, setTransferJobs] = useState(false);
  const [transferFrom, setTransferFrom] = useState('');
  const [unownedJobs, setUnownedJobs] = useState(0);
  const [companyJobs, setCompanyJobs] = useState(0);
  const [claimUnowned, setClaimUnowned] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [ownerEmails, setOwnerEmails] = useState<Record<string, string>>({});
  /*
   * What the picker opens with: the jobs this employer already holds, plus the unowned ones.
   *
   * Not "the unowned ones" alone, which is what an assign-only picker wanted. The dialog now
   * expresses an end state, so its initial ticks have to be the CURRENT state or confirming
   * without touching anything would release every job the employer already had.
   */
  const [pickerInitialIds, setPickerInitialIds] = useState<string[]>([]);

  const fetchRequestDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      if (typeof requestId !== 'string') throw new Error('Invalid requestId');
      const { data } = await fetchGraphQLApiData(companyRequestById(requestId));
      const req = data?.getCompanyRequest;
      setRequest(req);

      // Hoisted: the owner map built further down needs the requester's email too, and it is
      // assembled inside the companyId block.
      let requesterEmail = '';
      if (req?.employeeId) {
        const employerRes = await fetchGraphQLApiData(getEmployerById(req.employeeId));
        requesterEmail = employerRes?.data?.getEmployerById?.personalDetails?.email ?? '';
        setEmployerEmail(requesterEmail);
      }
      if (req?.companyId) {
        const companyRes = await fetchGraphQLApiData(companyById(req.companyId));
        setCompanyName(companyRes?.data?.getCompanyById?.companyName ?? '');

        const holdersRes = await fetchGraphQLApiData(employersByCompany(req.companyId)).catch(
          () => null,
        );
        const holders = (holdersRes?.data?.getAllEmployers?.content ?? []).filter(
          // The requester is excluded: transferring their own jobs to themselves is a no-op the
          // backend already ignores, and offering it here would just be confusing.
          (e: any) => e?._id && e._id !== req.employeeId,
        );
        setExistingHolders(holders);
        if (holders.length === 1) setTransferFrom(holders[0]._id);

        const unownedRes = await fetchGraphQLApiData(
          countUnownedCompanyJobs(req.companyId),
        ).catch(() => null);
        setUnownedJobs(unownedRes?.data?.countUnownedCompanyJobs ?? 0);

        const totalRes = await fetchGraphQLApiData(
          companyJobsCount(req.companyId),
        ).catch(() => null);
        setCompanyJobs(totalRes?.data?.getAllJobs?.totalElements ?? 0);

        // Every employer on this company, by id, so the picker can name the current owner of a
        // job rather than showing a raw id the admin cannot evaluate.
        const byId: Record<string, string> = {};
        (holdersRes?.data?.getAllEmployers?.content ?? []).forEach((e: any) => {
          if (e?._id) byId[e._id] = e?.personalDetails?.email ?? e._id;
        });
        if (req.employeeId) byId[req.employeeId] = requesterEmail || req.employeeId;
        setOwnerEmails(byId);
      }
    } catch {
      setError('Could not load this company request.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const decide = async (status: 'approved' | 'rejected') => {
    setLoading(true);
    setError(null);
    try {
      /*
       * Approval is one server-side call now, not two client-side ones.
       *
       * It used to set the status and then, separately, attach the employer to the company. The
       * second call is the one that grants anything, and nothing joined them: a failure between
       * them left a request reading "approved" whose employer was attached to nothing, and the
       * console could not fix it because the request was no longer pending. Rejection stays a
       * plain status update, because rejecting genuinely is only a record of a decision.
       */
      if (status === 'approved') {
        await fetchGraphQLApiData(
          approveCompanyRequest(
            requestId as string,
            transferJobs && transferFrom ? transferFrom : undefined,
            claimUnowned && unownedJobs > 0,
          ),
        );
      } else {
        await fetchGraphQLApiData(updateCompanyRequestStatus(requestId as string, status));
      }
      setRejectOpen(false);
      await fetchRequestDetails();
    } catch {
      setRejectOpen(false);
      setError(`Could not ${status === 'approved' ? 'approve' : 'reject'} this request.`);
      setLoading(false);
    }
  };

  /**
   * Rows for the picker: every job on this company, whoever owns it.
   *
   * The search is client-side because the list is one company's jobs -- capped at 200 and
   * typically dozens -- not a table that has to be filtered by the server. RecordPickerDialog
   * debounces it either way.
   */
  const searchCompanyJobs = useCallback(
    async (query: string): Promise<PickerRow[]> => {
      if (!request?.companyId) return [];
      const [res, appsRes] = await Promise.all([
        fetchGraphQLApiData(companyJobsForAssignment(request.companyId)).catch(
          () => null,
        ),
        fetchGraphQLApiData(companyApplicationCounts(request.companyId)).catch(
          () => null,
        ),
      ]);
      const jobs = res?.data?.getAllJobs?.content ?? [];

      /*
       * Applicants per job. Approving a request hands these jobs to an employer along with their
       * applicants, so the count belongs on the row that decides it.
       *
       * A truncated tally shows nothing rather than a low number: blank reads as "unknown",
       * "no applicants" reads as "safe", and they must not be confusable.
       */
      const apps = appsRes?.data?.getAllJobApplications;
      const complete =
        apps != null && (apps.totalElements ?? 0) <= (apps.content?.length ?? 0);
      const appCount: Record<string, number> = {};
      if (complete) {
        for (const a of apps.content ?? []) {
          if (a?.jobId) appCount[a.jobId] = (appCount[a.jobId] ?? 0) + 1;
        }
      }

      /*
       * Recomputed on every search rather than held in state: the picker reopens after a save,
       * and stale ticks would misdescribe who owns what -- which now matters far more, because
       * an unticked row releases a job rather than merely skipping it.
       */
      setPickerInitialIds(
        jobs
          .filter(
            (j: any) =>
              j.employerId === request?.employeeId ||
              !j.employerId ||
              j.employerId === 'null',
          )
          .map((j: any) => j._id),
      );

      const q = query.trim().toLowerCase();
      return jobs
        .filter((j: any) => !q || (j.title ?? '').toLowerCase().includes(q))
        .map((j: any) => {
          const owner = j.employerId && j.employerId !== 'null' ? j.employerId : '';
          const ownerLabel = owner
            ? `owned by ${ownerEmails[owner] ?? 'another account'}`
            : 'unassigned';
          return {
            id: j._id,
            title: j.title ?? j._id,
            subtitle: [
              j.jobStatus,
              j.postedDate ? formatDate(j.postedDate) : null,
              complete ? applicantLabel(appCount[j._id] ?? 0) : null,
            ]
              .filter(Boolean)
              .join(' · '),
            meta: ownerLabel,
          };
        });
    },
    [request?.companyId, ownerEmails],
  );

  const handleAssignSelected = async (ids: string[]) => {
    if (!request?.companyId || !request?.employeeId) return;
    setAssigning(true);
    setError(null);
    try {
      await fetchGraphQLApiData(
        setEmployerJobsOnCompany(request.companyId, request.employeeId, ids),
      );
      setPickerOpen(false);
      await fetchRequestDetails();
    } catch {
      setError('Could not assign the selected jobs.');
    } finally {
      setAssigning(false);
    }
  };

  if (loading && !request) return <SkeletonLoader />;
  if (error && !request) return <ErrorMessage page message={error} />;
  if (!request) return null;

  const isPending = isPendingRequest(request.status);
  const fmt = (v?: string) => formatDateTime(v);

  const identityRail = (
    <IdentityRail
      initials={(companyName || 'CR').slice(0, 2).toUpperCase()}
      name={companyName || 'Company request'}
      subId={request._id}
      statusChip={<CRMStatusChip status={request.status} />}
      glance={[
        { key: 'Employer', value: employerEmail || '-' },
        { key: 'Company', value: companyName || '-' },
        { key: 'Requested', value: fmt(request.createdAt), mono: true },
      ]}
      primaryAction={
        isPending ? (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={loading}
            onClick={() => decide('approved')}
          >
            Approve
          </Button>
        ) : undefined
      }
      secondaryActions={
        isPending ? (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            disabled={loading}
            onClick={() => setRejectOpen(true)}
          >
            Reject
          </Button>
        ) : undefined
      }
      audit={{ createdAt: request.createdAt, updatedAt: request.updatedAt }}
    />
  );

  return (
    <EntityWorkspace
      backLabel="Companies"
      onBack={() => navigate('/companies')}
      identityRail={identityRail}
      tabs={[{ id: 'details', label: 'Details' }]}
      activeTabId="details"
      onTabChange={() => {}}
      activityRail={null}
      activityOpen={activityOpen}
      onToggleActivity={() => setActivityOpen((o) => !o)}
    >
      {error && (
        <Box sx={{ mb: 'var(--spacing-md)' }}>
          <ErrorMessage message={error} />
        </Box>
      )}

      <SectionCard title="Request">
        <WorkspaceField label="Request ID" value={request._id} mono />
        <WorkspaceField label="Status" value={request.status} />
        <WorkspaceField label="Created" value={fmt(request.createdAt)} mono />
        <WorkspaceField label="Last updated" value={fmt(request.updatedAt)} mono />
      </SectionCard>

      <SectionCard title="Employer">
        <WorkspaceField label="Email" value={employerEmail || '-'} />
        <WorkspaceField label="Employer ID" value={request.employeeId} mono />
      </SectionCard>

      <SectionCard title="Company">
        <WorkspaceField label="Name" value={companyName || '-'} />
        <WorkspaceField label="Company ID" value={request.companyId} mono />
        <WorkspaceField
          label="Already attached"
          value={
            existingHolders.length === 0
              ? 'Nobody'
              : existingHolders
                  .map(
                    (e: any) =>
                      e?.personalDetails?.fullName ||
                      e?.personalDetails?.email ||
                      e._id,
                  )
                  .join(', ')
          }
        />
      </SectionCard>

      {/*
        Only offered while the decision is still open and somebody else actually holds the
        company. On a first registration there is nothing to move, and showing a disabled
        transfer control there would suggest a step the admin has skipped.
      */}
      {/*
        Jobs Mintly posted on this employer's behalf, before they had an account.

        Shown whether or not the request is still pending, unlike the transfer control above.
        That one is a decision taken at approval time; this is a repair. An approved request
        cannot be approved again -- the service treats a second approval as an idempotent
        double-click -- so every company claimed before this existed would otherwise need a
        database write to hand over its listings.
      */}
      {/*
        Shown whenever the company has jobs at all, not only unclaimed ones.
        Gating on unownedJobs > 0 hid the card the moment every job had been assigned -- which is
        exactly when an admin needs it, because that is the state a mistaken "assign all" leaves
        behind and there was then no route back.
      */}
      {companyJobs > 0 && (
        <SectionCard title="Jobs on this company">
          <Typography
            sx={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tx-mid)',
              mb: 'var(--spacing-sm)',
            }}
          >
            {companyJobs} job{companyJobs === 1 ? '' : 's'} on this company.{' '}
            {unownedJobs > 0
              ? `${unownedJobs} belong to no employer account, the usual shape when we have been posting on their behalf. Until they are assigned, this employer cannot edit them or see their applicants.`
              : 'All of them already have an owner. Use Choose jobs to change which ones belong to this employer.'}
          </Typography>
          {isPending && unownedJobs > 0 ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={claimUnowned}
                  onChange={(e) => setClaimUnowned(e.target.checked)}
                />
              }
              label={`Assign ${unownedJobs} unclaimed job${unownedJobs === 1 ? '' : 's'} to this employer on approval`}
            />
          ) : unownedJobs > 0 ? (
            <Button
              variant="outlined"
              disabled={loading || request.status !== 'approved'}
              onClick={async () => {
                setLoading(true);
                setError(null);
                try {
                  await fetchGraphQLApiData(
                    assignUnownedCompanyJobs(request.companyId, request.employeeId),
                  );
                  await fetchRequestDetails();
                } catch {
                  setError('Could not assign the unclaimed jobs.');
                  setLoading(false);
                }
              }}
            >
              {`Assign all ${unownedJobs} unclaimed job${unownedJobs === 1 ? '' : 's'}`}
            </Button>
          ) : null}
          {/*
            The considered path beside the one-click one, and the only route that can take a job
            back. "Assign all" is right when Mintly has been posting for a company and every
            listing is theirs; this is for every other case, including undoing that.
          */}
          <Box sx={{ mt: 'var(--spacing-sm)' }}>
            <Button variant="text" onClick={() => setPickerOpen(true)}>
              Choose jobs…
            </Button>
          </Box>
        </SectionCard>
      )}

      <RecordPickerDialog
        open={pickerOpen}
        multiple
        title={`Jobs for ${employerEmail || 'this employer'}`}
        searchPlaceholder="Search jobs on this company…"
        confirmLabel="Save"
        onSearch={searchCompanyJobs}
        onConfirmMany={handleAssignSelected}
        onClose={() => setPickerOpen(false)}
        submitting={assigning}
        initialSelectedIds={pickerInitialIds}
      />

      <ConfirmDialog
        open={rejectOpen}
        title={REJECT_TITLE}
        body={rejectBody(employerEmail, companyName)}
        actionLabel="Reject"
        busy={loading}
        onCancel={() => setRejectOpen(false)}
        onConfirm={() => decide('rejected')}
      />

      {isPending && existingHolders.length > 0 && (
        <SectionCard title="Existing jobs">
          <Typography
            sx={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tx-mid)',
              mb: 'var(--spacing-sm)',
            }}
          >
            Approving attaches this employer to the company. It does not move anything that is
            already there. If this is a handover rather than a second contact, move the jobs too:
            otherwise they stay with the previous account, which means the new employer cannot
            edit them or see their applicants.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={transferJobs}
                onChange={(e) => setTransferJobs(e.target.checked)}
              />
            }
            label="Transfer existing jobs to this employer"
          />
          {transferJobs && (
            <Box sx={{ mt: 'var(--spacing-sm)' }}>
              <SelectFormField
                required={false}
                name="transferFrom"
                label="Move jobs from"
                value={transferFrom}
                onChange={(_n, v) => setTransferFrom(String(v))}
                options={existingHolders.map((e: any) => ({
                  value: e._id,
                  label:
                    e?.personalDetails?.fullName ||
                    e?.personalDetails?.email ||
                    e._id,
                }))}
              />
            </Box>
          )}
        </SectionCard>
      )}
    </EntityWorkspace>
  );
};

export default CompanyRequests;
